<template>
  <v-container fluid class="no-padding-container">
    <!-- Título -->
    <v-row v-if="hideChip && produtosFiltrados.length > 0">
      <v-col cols="12" class="pa-0 ma-0">
        <h1 class="section-title">Principais Resultados</h1>
      </v-col>
    </v-row>

    <!-- Chips de categoria -->
    <v-row v-if="!hideChip">
      <v-col cols="12" class="pa-0 ma-0 d-flex justify-center chip-container">
        <v-slide-group v-model="selectedCategoryId">
          <v-slide-group-item :value="null" v-slot="{ isSelected }">
            <v-chip
              rounded="pill"
              variant="text"
              class="chip-custom"
              :class="{ selected: isSelected || selectedCategoryId === null }"
              @click="selecionarCategoria(null)"
            >
              Tudo
            </v-chip>
          </v-slide-group-item>
          <v-slide-group-item
            v-for="categoria in categorias.filter((c) => !c.disabled)"
            :key="categoria.value"
            :value="categoria.value"
            v-slot="{ isSelected }"
          >
            <v-chip
              variant="text"
              rounded="pill"
              class="chip-custom"
              :class="{ selected: isSelected }"
              @click="selecionarCategoria(categoria.value)"
            >
              {{ categoria.title }}
            </v-chip>
          </v-slide-group-item>
        </v-slide-group>
      </v-col>
    </v-row>

    <!-- Carregando produtos -->
    <v-row
      v-if="loading"
      class="fill-height"
      align-content="center"
      justify="center"
    >
      <v-col class="text-subtitle-1 text-center" cols="12">
        Carregando produtos...
      </v-col>
      <v-col cols="6">
        <v-progress-linear color="primary" indeterminate rounded height="6" />
      </v-col>
    </v-row>

    <!-- Lista de produtos -->
    <template v-if="!loading">
      <div
        v-if="produtosBase.length > 0"
        :class="[
          'flex-lista-produtos',
          { 'produtos-filtrados': hideChip && produtosFiltrados.length > 0 },
        ]"
      >
        <ProductCard
          v-for="produto in produtosVisiveis"
          :key="produto.id"
          :produto="produto"
          :resources-url="resourcesUrl"
          :categorias-por-id="categoriasPorId"
        />
      </div>

      <v-row v-else-if="buscaInicialCompleta">
        <v-col cols="12" class="pa-0 ma-0 d-flex justify-center">
          <ProductsNotFound :isPageSearch="namePage === 'search' ? true : false" />
        </v-col>
      </v-row>
    </template>

    <!-- Botão "Mostrar mais" -->
    <v-row v-if="!loading && hasMoreToShow">
      <v-col cols="12" class="d-flex justify-center button-more">
        <v-btn
          @click="loadMore"
          :loading="loadingMore"
          class="gradient-button"
          rounded="pill"
          variant="outlined"
        >
          Mostrar mais
        </v-btn>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import { onMounted, ref, watch, computed } from "vue";
import { useProdutos } from "@/composables/useProdutos";
import ProductCard from "./ProductCard.vue";
import ProductsNotFound from "@/components/ProductsNotFound.vue";

const props = defineProps({
  hideChip: Boolean,
  hideTitle: Boolean,
  filtrarPorTermo: Boolean,
  termo: String,
  namePage: String
});
const {
  produtos,
  loading,
  loadingMore,
  filtros,
  hasMoreProducts,
  categorias,
  categoriasPorId,
  carregarProdutos,
  carregarCategorias,
  carregarMaisProdutos,
} = useProdutos();

const selectedCategoryId = ref(null);
const produtosFiltrados = ref([]);
const buscaInicialCompleta = ref(false);
const resourcesUrl = import.meta.env.VITE_RESOURCES_URL || "";

const produtosAtivos = computed(() =>
  produtos.value.filter((produto) => !produto.disabled)
);

const selecionarCategoria = (categoriaId) => {
  selectedCategoryId.value = categoriaId;
  filtros.value.categoria = categoriaId;
};

// Controle de produtos visíveis
const produtosVisiveisCount = ref(4);

const produtosBase = computed(() =>
  props.filtrarPorTermo ? produtosFiltrados.value : produtosAtivos.value
);

const produtosVisiveis = computed(() => {
  // Retorna uma fatia da lista de produtos com base na contagem de itens visíveis
  return produtosBase.value.slice(0, produtosVisiveisCount.value);
});

const hasMoreToShow = computed(() => {
  return produtosVisiveis.value.length < produtosBase.value.length;
});

const loadMore = () => {
  // Adiciona mais 4 produtos à contagem de itens visíveis
  produtosVisiveisCount.value += 4;
};

const filtrarProdutosPorTermo = (termo) => {
  if (!props.filtrarPorTermo) return;

  if (!termo) {
    produtosFiltrados.value = [...produtosAtivos.value];
    return;
  }

  const normalizarTexto = (texto) =>
    texto?.toString().toLowerCase().replace(/\s+/g, "") || "";

  const termoNormalizado = normalizarTexto(termo);
  const ehNumero = !isNaN(termoNormalizado) && termoNormalizado !== "";

  produtosFiltrados.value = produtosAtivos.value.filter((produto) => {
    const nome = normalizarTexto(produto.nome);
    const descricao = normalizarTexto(produto.descricao);
    const id = produto.id?.toString();

    if (ehNumero) {
      return id === termoNormalizado;
    }

    return (
      nome.includes(termoNormalizado) || descricao.includes(termoNormalizado)
    );
  });
};

onMounted(async () => {
  await carregarCategorias();
  await carregarProdutos(0, 9999, true); // Carrega todos os produtos para a vitrine
  if (props.filtrarPorTermo && props.termo) {
    filtrarProdutosPorTermo(props.termo);
  } else {
    produtosFiltrados.value = [...produtosAtivos.value];
  }

  buscaInicialCompleta.value = true;
});

watch(
  () => props.termo,
  (novoTermo) => {
    if (props.filtrarPorTermo) {
      filtrarProdutosPorTermo(novoTermo);
      produtosVisiveisCount.value = 4; // Reseta a contagem ao buscar
    }
  },
  { immediate: true }
);

// Observa a lista de produtos principal que vem do composable.
// Quando o filtro de categoria é alterado, o composable busca novos produtos na API,
// atualizando `produtos`. Este watch garante que a lista local (`produtosFiltrados`)
// seja sincronizada com os novos dados.
watch(produtos, (novosProdutos) => {
  produtosFiltrados.value = [...novosProdutos.filter(p => !p.disabled)];
  filtrarProdutosPorTermo(props.termo); // Re-aplica o filtro de texto se houver
  produtosVisiveisCount.value = 4; // Reseta a contagem para a primeira "página"
});

defineExpose({
  filtrarProdutosPorTermo,
  buscarProdutos: carregarProdutos,
});
</script>

<style scoped>
.no-padding-container {
  padding: 0 !important;
  margin: 0 !important;
  max-width: 100%;
  width: 100%;
}

.flex-lista-produtos {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-start;
  padding: 0 80px 0px 80px !important;
}

.flex-lista-produtos > * {
  flex: 0 0 calc((100% - 3 * 32px) / 4);
  max-width: calc((100% - 3 * 32px) / 4);
}

.section-title {
  font-family: "Ubuntu", sans-serif;
  font-weight: 700;
  font-size: 24px;
  color: #0a4488;
  padding: 46px 0 24px 0 !important;
}

.more-button {
  border: 1px solid #fb631a;
  color: #fb631a !important;
}

.chip-custom {
  background-color: transparent !important;
  color: #102954 !important;
  border: none !important;
  font-weight: 400;
  text-transform: uppercase;
  box-shadow: none !important;
  font-family: "Ubuntu", sans-serif;
  margin: 16px 0 24px 0 !important;
  font-size: 12px;
}

.chip-custom.selected {
  background-color: #f4923c !important;
  color: #fff !important;
}

.gradient-button {
  background: transparent !important;
  border: 2px solid #fb631a !important;
  color: #fb631a !important;
  position: relative;
  font-weight: 500 !important;
  font-size: 14px !important;
  font-family: "Ubuntu", sans-serif;
  width: 180px !important;
}

.gradient-button:hover {
  background: linear-gradient(90deg, #fb631a, #fc901f) !important;
  color: white !important;
  border-color: transparent !important;
}

.gradient-button:hover .v-btn__content {
  background: none !important;
  -webkit-background-clip: unset !important;
  background-clip: unset !important;
  color: white !important;
}

.button-more {
  margin-bottom: 32px !important;
  margin-top: 16px !important;
}

/* Desktop (padrão) */
/* Nada muda aqui */

/* Tablets */
@media (max-width: 1024px) {
  .flex-lista-produtos > * {
    flex: 0 0 calc((100% - 1 * 8px) / 2);
    max-width: calc((100% - 1 * 8px) / 2);
  }

  .flex-lista-produtos {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-start;
    padding: 0 40px 0 40px !important;
  }


  .section-title {
    padding: 46px 12px 24px 12px !important;
  }

  .button-more {
    margin-bottom: 46px !important;
    margin-top: 24px !important;
  }
}

.produtos-filtrados {
  padding: 16px 0 0 0 !important;
}
</style>
